import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import { getNotificationDate } from 'utils/dates';
import { ContentWrapper, CloseButton, ContentPlaceholder } from 'components';
import {
  getAuthUser,
  getNotifications,
  getNotificationsUnreadCount,
} from 'store/selectors/auth';
import { doMarkNotificationsAsRead } from 'store/actionCreators/profile';
import { UserNotificationItem } from 'types';
import { getCommunities } from 'store/selectors/communities';

import {
  StyledHeader,
  StyledContent,
  NotificationWrapper,
  NotificationDate,
  Message,
  MessageWrapper,
  NotificationTitle,
  StatusIndicator,
  StyledArrowIcon,
  NotificationImage,
} from './styled';

const NotificationsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(getAuthUser);
  const notifications = useSelector(getNotifications) || [];
  const notificationsUnreadCount = useSelector(getNotificationsUnreadCount);
  const communities = useSelector(getCommunities);

  useEffect(() => {
    if (notificationsUnreadCount) {
      dispatch(
        doMarkNotificationsAsRead({
          userId: user.userId,
          notifications: [],
        }),
      );
    }
  }, [notifications]);

  const goToProfile = () => {
    navigate('/profile');
  };

  const getCommunityLabel = useCallback(
    (notification: UserNotificationItem) => {
      const community = communities.find(
        (c) => c.id === notification.communityId,
      );

      if (community) {
        return `${community.name} • `;
      }

      return '';
    },
    [notifications, communities],
  );

  const formatNotificationLink = (link: string) => {
    if (link) {
      return link.replace('communities', 'community');
    }

    return null;
  };

  return (
    <ContentWrapper>
      <StyledHeader>
        {t('common.notifications')}
        <CloseButton hidden={false} onClick={goToProfile} />
      </StyledHeader>
      <StyledContent>
        {isEmpty(notifications) ? (
          <ContentPlaceholder
            placeholder={t('profileSettings.notificationsPlaceholder')}
          />
        ) : (
          notifications.map((notification) => (
            <NotificationWrapper
              key={notification.id}
              href={formatNotificationLink(notification.link)}
              isRead={notification.isRead}
            >
              <MessageWrapper>
                <StatusIndicator isRead={notification.isRead} />
                {notification.image ? (
                  <NotificationImage src={notification.image} />
                ) : null}
                <Message>
                  {notification.createdAt && (
                    <NotificationDate>
                      {`${getCommunityLabel(notification)}${getNotificationDate(
                        t,
                        notification.createdAt,
                      )}`}
                    </NotificationDate>
                  )}
                  {notification.title ? (
                    <NotificationTitle>{notification.title}</NotificationTitle>
                  ) : null}
                  {notification.message ? (
                    <div>{notification.message}</div>
                  ) : null}
                </Message>
              </MessageWrapper>
              {notification.link && <StyledArrowIcon />}
            </NotificationWrapper>
          ))
        )}
      </StyledContent>
    </ContentWrapper>
  );
};

export default NotificationsPage;
